import React, { useState, useEffect, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import MonthDays from '../components/MonthDays';
import EventListView from '../components/EventListView';
import config from '../config';
import { parseEventSlug, generateEventSlug } from '../utils/eventUtils';
import { themes } from '../config/themes';
import OrganizationFilter from '../components/OrganizationFilter';
import EventMapView from '../components/EventMapView';
import featureFlags from '../config/featureFlags';

function HomeOld() {
  // get the current month and year
  var thisDate = new Date();   
  var thisMonth = thisDate.getMonth() + 1;
  const thisYear = thisDate.getFullYear();

  const theme = themes['light'];

  // Set initial view mode based on feature flags
  const getInitialViewMode = () => {
    if (featureFlags.calendar.enabled) return 'calendar';
    if (featureFlags.list.enabled) return 'list';
    if (featureFlags.map.enabled) return 'map';
    return 'calendar'; // fallback
  };
  
  const [curMonth, setValue] = useState(thisMonth);
  const [curYear, setYear] = useState(thisYear);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [viewMode, setViewMode] = useState(getInitialViewMode());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [events, setEvents] = useState([]);
  const [selectedEventSlug, setSelectedEventSlug] = useState(window.location.hash.slice(1) || null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [toast, setToast] = useState(null);
  const toastTimeout = useRef(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showFilters, setShowFilters] = useState(false);  


  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (viewMode === 'calendar' && !isModalOpen) {
        setSwipeDirection('right');
        setIsAnimating(true);
        handleIncrement();
        setTimeout(() => {
          setIsAnimating(false);
          setSwipeDirection(null);
        }, 300);
      }
    },
    onSwipedRight: () => {
      if (viewMode === 'calendar' && !isModalOpen) {
        setSwipeDirection('left');
        setIsAnimating(true);
        handleDecrement();
        setTimeout(() => {
          setIsAnimating(false);
          setSwipeDirection(null);
        }, 300);
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  // Handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        // First try to find the event directly if we have events loaded
        if (events.length > 0) {
          const event = events.find(e => generateEventSlug(e) === hash.toLowerCase());
          if (event) {
            setSelectedEventSlug(hash);
            setShouldOpenModal(true);
            return;
          }
        }
        // If we can't find the event, try to parse the date
        const dateInfo = parseEventSlug(hash);
        if (dateInfo) {
          setValue(dateInfo.month);
          setYear(dateInfo.year);
          setSelectedEventSlug(hash);
          setShouldOpenModal(true);
        }
      } else {
        setSelectedEventSlug(null);
        setShouldOpenModal(false);
      }
    };

    // Handle initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [events]);

  // Handle event selection from URL after events are loaded
  useEffect(() => {
    if (selectedEventSlug && events.length > 0 && initialLoadComplete) {
      const event = events.find(e => generateEventSlug(e) === selectedEventSlug.toLowerCase());
      if (event) {
        // Ensure we're in calendar view when showing an event
        setViewMode('calendar');
        setSelectedEventSlug(selectedEventSlug);
        if (shouldOpenModal) {
          setIsModalOpen(true);
          setShouldOpenModal(false);
        }
      } else {
        // If we can't find the event in the current month, try fetching it
        const dateInfo = parseEventSlug(selectedEventSlug);
        if (dateInfo && (dateInfo.month !== curMonth || dateInfo.year !== curYear)) {
          setValue(dateInfo.month);
          setYear(dateInfo.year);
        }
      }
    }
  }, [selectedEventSlug, events, initialLoadComplete, curMonth, curYear, shouldOpenModal]);



  useEffect(() => {
    const fetchEvents = async () => {
      setIsLoading(true);
      // Check cache first
      const cacheKey = `events-${curYear}-${curMonth}`;
      const versionKey = 'events-api-version';
      const cachedData = localStorage.getItem(cacheKey);
      const cachedVersion = localStorage.getItem(versionKey);
      
      try {
        // Check API version first
        const versionResponse = await fetch(`${config.apiBaseUrl}${config.endpoints.events.version}`);
        const currentVersion = await versionResponse.text();

        // If version changed, clear all event caches
        if (cachedVersion !== currentVersion) {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('events-')) {
              localStorage.removeItem(key);
            }
          });
          localStorage.setItem(versionKey, currentVersion);
        } else if (cachedData) {
          setEvents(JSON.parse(cachedData));
          setIsLoading(false);
          setInitialLoadComplete(true);
          return;
        }

        // Fetch events if cache was invalid or cleared
        const response = await fetch(`${config.apiBaseUrl}${config.endpoints.events.monthly}/${curYear}/${curMonth}`);
        const data = await response.json();
        // Store in cache
        localStorage.setItem(cacheKey, JSON.stringify(data));
        setEvents(data);
      } catch (error) {
        console.error('Error fetching events:', error);
        if (cachedData) {
          setEvents(JSON.parse(cachedData));
        } else {
          setEvents([]); // Set empty array if no cache and error
        }
      } finally {
        setIsLoading(false);
        setInitialLoadComplete(true);
      }
    };

    fetchEvents();
  }, [curMonth, curYear]);

  // Extract unique organizations whenever events change
  useEffect(() => {
    if (events && events.length > 0) {
      const orgs = [...new Set(events.map(event => event.organization))].sort();
      setOrganizations(orgs);
    } else {
      setOrganizations([]);
    }
  }, [events]);

  // Filter events based on selected organization
  const filteredEvents = selectedOrg === 'all' 
    ? events 
    : events.filter(event => event.organization === selectedOrg);

  // Clear selected event when navigating away
  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const gotoToday = () => {
    setValue(thisMonth);
    setYear(thisYear);
    setShowMonthPicker(false);
  }

  const setCurMonth = (month) => {
    setValue(month);
  }

  const handleIncrement = () => {
    setValue(prevValue => prevValue + 1);
    setShowMonthPicker(false);
    if (curMonth === 12) {
      setValue(1);
      setYear(prevValue => prevValue + 1);
    }
  }

  const handleDecrement = () => {
    setValue(prevValue => prevValue - 1);
    setShowMonthPicker(false);
    if (curMonth === 1) {
      setValue(12);
      setYear(prevValue => prevValue - 1);
    }
  }

  // Replace the toggleViewMode function with individual view mode setters
  const setCalendarView = () => {
    if (featureFlags.calendar.enabled) {
      setViewMode('calendar');
    }
  };
  
  const setListView = () => {
    if (featureFlags.list.enabled) {
      setViewMode('list');
    }
  };
  
  const setMapView = () => {
    if (featureFlags.map.enabled) {
      setViewMode('map');
    }
  };

  const renderMonthName = () => {
    const date = new Date(curYear, curMonth - 1, 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Show toast for 3 seconds
  const showToast = (message) => {
    setToast(message);
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(() => setToast(null), 3000);
  };

  // Watch for month/org changes and auto-switch if no events for org
  useEffect(() => {
    if (selectedOrg !== 'all' && !isLoading && initialLoadComplete) {
      const orgEvents = events.filter(event => event.organization === selectedOrg);
      if (orgEvents.length === 0) {
        showToast('No events this month for the selected organization, switching to All Organizations.');
        setSelectedOrg('all');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curMonth, curYear, selectedOrg, events, isLoading, initialLoadComplete]);

  const handleEventSelect = (event) => {
    setSelectedEvent(event);
    setSelectedEventSlug(generateEventSlug(event));
    setIsModalOpen(true);
    // Only set view mode to calendar if we're not in map view
    if (viewMode !== 'map') {
      setViewMode('calendar');
    }
  };

  return (
    <div className="flex justify-center w-full overflow-x-hidden touch-pan-y">
      <div  className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">   
        

        <div id="month-picker-container" className="sm:flex flex-wrap justify-between items-center mb-4 text-xl font-bold bg-blue-300 p-2 rounded-lg w-full">

          <div className="text-sm hidden sm:block font-bold  ">
            
          </div>
          
          <div className="flex flex-col sm:flex-row items-center">
            <div className="flex items-center w-full sm:w-auto">
              <button 
                    onClick={handleDecrement} 
                    title="Previous Month" 
                    className="flex h-10 w-10 sm:rounded-lg py-2 px-3 items-center justify-center cursor-pointer hover:bg-slate-400 xs:bg-slate-300"
                    aria-label="Go to previous month"
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true"></i>
                  </button>
                <button 
                  id="month-picker-button"
                  onClick={() => setShowMonthPicker(!showMonthPicker)} 
                  className="sm:flex text-xl w-full sm:w-auto font-bold items-center cursor-pointer sm:rounded-lg px-3 py-2 h-10 hover:bg-slate-400 xs:bg-slate-300"
                  aria-haspopup="true"
                  aria-expanded={showMonthPicker}
                >
                  {renderMonthName()} <i className="fas fa-caret-down ml-2" aria-hidden="true"></i>
                </button>
                <button 
                    onClick={handleIncrement} 
                    title="Next Month" 
                    className="flex h-10 w-10 sm:rounded-lg py-2 px-3 items-center justify-center cursor-pointer hover:bg-slate-400 xs:bg-slate-300 mr-3"
                    aria-label="Go to next month"
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                  </button>
                </div>
                {showMonthPicker && (
                  <div className="absolute z-50 bg-white shadow-lg rounded-lg p-2 mt-2" style={{ top: document.getElementById('month-picker-button')?.getBoundingClientRect().bottom + window.scrollY + 'px', left: document.getElementById('month-picker-button')?.getBoundingClientRect().left + 'px' }}>
                      <button 
                        onClick={gotoToday} 
                        title="Today" 
                        className="flex h-10 w-full sm:rounded-lg py-2 px-3 items-center justify-center bg-slate-300 cursor-pointer hover:bg-slate-400"
                        aria-label="Go to today's date"
                      >
                        Today
                      </button>
                    <div className="grid grid-cols-3 gap-2">
                      {Array.from({ length: 12 }, (_, i) => (
                        <button
                          key={i+1}
                          onClick={() => {
                            setCurMonth(i+1); 
                            setShowMonthPicker(false);
                          }}
                          className={`p-2 rounded-lg ${
                            curMonth === i+1 
                              ? 'bg-blue-500 text-white' 
                              : new Date().getMonth() + 1 === i+1 && new Date().getFullYear() === curYear
                                ? 'bg-slate-200' 
                                : 'hover:bg-blue-100'
                          }`}
                        >
                          {new Date(0, i).toLocaleString('default', { month: 'short' })}
                        </button>
                      ))}
                          <button 
                            onClick={() => setYear(prevYear => prevYear - 1)}
                            disabled={curYear === 2025}
                            className={curYear === 2025 ? "opacity-50 cursor-not-allowed" : ""}
                            aria-label={curYear === 2025 ? "Cannot go back before 2025" : "Previous year"}
                          >
                          <i className="fas fa-chevron-left" aria-hidden="true"></i>
                        </button>
                        <span>{curYear}</span>
                        <button 
                          onClick={() => setYear(prevYear => prevYear + 1)}
                          disabled={curYear === 2025}
                          className={curYear === 2025 ? "opacity-50 cursor-not-allowed" : ""}
                          aria-label={curYear === 2025 ? "No events yet for this year" : "Next year"}
                          title={curYear === 2025 ? "No events yet for this year" : "Next year"}
                        >
                          <i className="fas fa-chevron-right" aria-hidden="true"></i>
                        </button> 
                    </div>
                  </div>
                )}
            <div className="flex items-center space-x-2 mt-2 sm:mt-0">
              {featureFlags.calendar.enabled && (
                <button 
                  onClick={setCalendarView}
                  className={`py-2 px-4 rounded-lg transition flex items-center ${
                    viewMode === 'calendar' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                  aria-label="Calendar view"
                  title="Calendar view"
                  aria-pressed={viewMode === 'calendar'}
                >
                  <i className="fas fa-calendar" aria-hidden="true" aria-label="Calendar view"></i>
                </button>
              )}

              {featureFlags.list.enabled && (
                <button 
                  onClick={setListView}
                  className={`py-2 px-4 rounded-lg transition flex items-center ${
                    viewMode === 'list' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                  aria-label="List view"
                  title="List view"
                  aria-pressed={viewMode === 'list'}
                >
                  <i className="fas fa-list" aria-hidden="true" aria-label="List view"></i>
                </button>
              )}

              {featureFlags.map.enabled && (
                <button 
                  onClick={setMapView}
                  className={`py-2 px-4 rounded-lg transition flex items-center ${
                    viewMode === 'map' 
                      ? 'bg-blue-500 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                  aria-label="Map view"
                  title="Map view"
                  aria-pressed={viewMode === 'map'}
                >
                  <i className="fas fa-map-marker-alt" aria-hidden="true" aria-label="Map view"></i>
                </button>
              )}

              <button
                id="filter-button"
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`py-2 px-4 rounded-lg transition flex items-center ${
                  showFilters === true 
                    ? selectedOrg !== 'all' 
                      ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-700'
                      : 'bg-gray-500 hover:bg-gray-200 text-gray-200 hover:text-gray-700'
                    : selectedOrg !== 'all' 
                      ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-700'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
                aria-pressed={showFilters}
                aria-label="Toggle organization filter panel"
                title={selectedOrg !== 'all' ? `Filtered by ${selectedOrg}` : 'No filters applied'}
              >
                <i className={`fas fa-filter ${selectedOrg !== 'all' ? 'underline' : ''}`} aria-hidden="true" aria-label="Toggle filters"></i>
                
              </button>

            </div>
          </div>
        </div>

        {/* Add organization filter above the views */}
        {showFilters && (
          <div className={`rounded-lg shadow-md opacity-[.93] ${theme.gridBg} p-4 mb-4`}>
            <OrganizationFilter
              organizations={organizations}
              selectedOrg={selectedOrg}
              onOrgChange={setSelectedOrg}
              theme={theme}
            />
          </div>
        )}

        {/* Toast notification */}
        {toast && (
          <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-200 text-yellow-900 px-6 py-3 rounded shadow-lg text-center font-semibold text-sm animate-fade-in-out">
            {toast}
          </div>
        )}

        <div className="w-full relative" {...handlers}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10" role="status" aria-live="polite">
              <div className="flex flex-col items-center">
                <i className="fas fa-circle-notch fa-spin text-4xl text-blue-600 mb-2" aria-hidden="true"></i>
                <span className="text-blue-600">Loading events...</span>
              </div>
            </div>
          )}

          {/* Modal backdrop layer for MonthDays modal */}
          {isModalOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-30 z-15 cursor-pointer"
              aria-label="Modal backdrop"
              onClick={handleModalClose}
            />
          )}

          {viewMode === 'calendar' ? (
            <div 
              className={`transition-transform duration-300 ease-in-out ${
                isAnimating
                  ? swipeDirection === 'left'
                    ? 'translate-x-[-100%] opacity-0'
                    : 'translate-x-[100%] opacity-0'
                  : 'translate-x-0 opacity-100 w-full relative'
              }`}
              role="region"
              aria-label="Calendar view"
            >
              <MonthDays 
                curMonth={curMonth} 
                curYear={curYear} 
                events={filteredEvents}
                onModalStateChange={setIsModalOpen}
                selectedEventSlug={selectedEventSlug}
                onModalClose={handleModalClose}
                isModalOpen={isModalOpen}
                theme={theme}
              />
            </div>
          ) : viewMode === 'list' ? (
            <div role="region" aria-label="List view">
              <EventListView 
                events={filteredEvents}
                curMonth={curMonth}
                curYear={curYear}
              />
            </div>
          ) : (
            <div role="region" aria-label="Map view">
              <EventMapView 
                theme={theme}
                events={filteredEvents}
                // organizations={organizations}
                // selectedOrg={selectedOrg}
                // onOrgChange={setSelectedOrg}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

  export default HomeOld; 